import { useCallback, useEffect, useState } from "react";
import { Radar, Package } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, openDownload } from "@/lib/api";
import { Section } from "./Section";
import { InstallMap } from "./InstallMap";

const BADGE = {
  ok: "bg-emerald-900/40 text-emerald-300 border-emerald-800", unknown_key: "bg-red-900/40 text-red-300 border-red-800",
  invalid_signature: "bg-red-900/40 text-red-300 border-red-800", build_mismatch: "bg-red-900/40 text-red-300 border-red-800",
  revoked: "bg-slate-800 text-slate-400 border-slate-600", expired: "bg-slate-800 text-slate-400 border-slate-600",
  hw_mismatch: "bg-orange-900/40 text-orange-300 border-orange-800", multi_hw: "bg-orange-900/40 text-orange-300 border-orange-800",
  crl: "bg-slate-800 text-slate-400 border-slate-700",
};
const LABEL = { ok: "OK", unknown_key: "nieznany klucz", invalid_signature: "podrobiony podpis", build_mismatch: "obca paczka", revoked: "odwołana",
  expired: "wygasła", hw_mismatch: "inny sprzęt", multi_hw: "kopia (wiele HWID)", multi_domain: "wiele domen", revoked_contact: "odwołana się melduje", crl: "pobranie CRL", dead: "nie odzywa się" };
const Badge = ({ s }) => <span className={`text-[10px] border rounded px-1.5 py-0.5 mr-1 ${BADGE[s] || BADGE.hw_mismatch}`}>{LABEL[s] || s}</span>;
const ago = (ts) => {
  if (!ts) return "—";
  const m = Math.round((Date.now() - new Date(ts)) / 60000);
  return m < 60 ? `${m} min temu` : m < 2880 ? `${Math.round(m / 60)} godz. temu` : `${Math.round(m / 1440)} dni temu`;
};

function Tiles({ st }) {
  if (!st) return null;
  const t = [["Kontakty dziś", st.today], ["Kontakty 7 dni", st.d7], ["Aktywne instalacje", st.installations_active], ["Alerty", st.alerts, st.alerts > 0]];
  return <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">{t.map(([l, v, red]) => (
    <div key={l} className={`rounded p-2 border ${red ? "border-red-700 bg-red-950/40" : "border-slate-700 bg-slate-900/50"}`} data-testid={`licmon-tile-${l}`}>
      <div className="text-[10px] text-slate-400 uppercase">{l}</div><div className={`text-lg font-bold font-mono ${red ? "text-red-300" : ""}`}>{v ?? 0}</div></div>))}</div>;
}

function Installs({ rows, onRevoke }) {
  return (
    <div className="overflow-x-auto mb-3"><table className="w-full text-[11px]" data-testid="licmon-installations">
      <thead><tr className="text-slate-500 text-left"><th>Firma</th><th>Domena / IP</th><th>HWID</th><th>Wersja</th><th>Build</th><th>Ostatni kontakt</th><th>Status</th><th /></tr></thead>
      <tbody>{rows.length === 0 && <tr><td colSpan={8} className="text-slate-500 py-3">Brak zarejestrowanych kontaktów.</td></tr>}
        {rows.map((r) => {
          const bad = (r.flags || []).length || r.dead || r.last_status !== "ok";
          return (
            <tr key={r.id} className={`border-t border-slate-700 ${bad ? "bg-red-950/30" : ""}`} data-testid={`licmon-inst-${r.id}`}>
              <td>{r.company || "— nieznany klucz —"}</td><td className="font-mono">{r.last_domain || r.last_ip}</td><td className="font-mono" title={r.hw}>{(r.hw || "—").slice(0, 12)}</td>
              <td>{r.last_version || "—"}</td><td className="font-mono">{r.build_id || "—"}</td><td>{ago(r.last_seen)}</td>
              <td><Badge s={r.last_status} />{r.dead && <Badge s="dead" />}{(r.flags || []).filter((f) => f !== r.last_status).map((f) => <Badge key={f} s={f} />)}</td>
              <td>{r.key_known && !r.revoked && <button onClick={() => onRevoke(r)} className="text-red-300 hover:underline" data-testid={`licmon-revoke-${r.id}`}>Odwołaj licencję</button>}</td>
            </tr>
          );
        })}</tbody></table></div>
  );
}

function Log() {
  const [f, setF] = useState({ status: "", flagged: false, q: "" });
  const [d, setD] = useState({ items: [], total: 0 });
  const load = useCallback(() => {
    const p = new URLSearchParams({ size: "200", ...(f.status ? { status: f.status } : {}), ...(f.flagged ? { flagged: "true" } : {}), ...(f.q ? { q: f.q } : {}) });
    api.get(`/license/contacts?${p}`).then((r) => setD(r.data)).catch(() => {});
  }, [f]);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="licmon-log">
      <div className="flex flex-wrap gap-2 mb-2 text-[11px]">
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className="bg-slate-900 border border-slate-700 rounded px-2 py-1" data-testid="licmon-f-status">
          <option value="">wszystkie statusy</option>{Object.keys(BADGE).map((s) => <option key={s} value={s}>{LABEL[s]}</option>)}</select>
        <label className="flex items-center gap-1"><input type="checkbox" checked={f.flagged} onChange={(e) => setF({ ...f, flagged: e.target.checked })} data-testid="licmon-f-flagged" />tylko podejrzane</label>
        <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="IP, HWID, firma, build…" className="bg-slate-900 border border-slate-700 rounded px-2 py-1 flex-1 min-w-[140px]" data-testid="licmon-f-q" />
        <span className="text-slate-500 self-center">{d.total} wpisów</span>
      </div>
      <div className="max-h-80 overflow-y-auto space-y-0.5">{d.items.map((c) => (
        <div key={c.id} className="flex flex-wrap gap-x-3 text-[11px] border-b border-slate-800 py-0.5" data-testid={`licmon-contact-${c.id}`}>
          <span className="text-slate-500 w-32">{c.at.slice(0, 16).replace("T", " ")}</span><span className="w-40 truncate">{c.company || "— nieznany klucz —"}</span>
          <span><Badge s={c.status} />{(c.flags || []).filter((x) => x !== c.status).map((x) => <Badge key={x} s={x} />)}</span>
          <span className="font-mono">{c.ip}</span><span>{c.version || ""}</span><span className="font-mono text-slate-400">{c.build_id || "—"}</span><span className="text-slate-500">{c.endpoint}</span>
        </div>))}</div>
    </div>
  );
}

function ImageBuilds({ reload }) {
  const [rows, setRows] = useState([]);
  const [rk, setRk] = useState(0);
  useEffect(() => {
    let stop = false;
    const tick = () => api.get("/license/image-builds").then((r) => { if (!stop) setRows(r.data); }).catch(() => {});
    tick();
    const id = setInterval(tick, 10000);
    return () => { stop = true; clearInterval(id); };
  }, [reload, rk]);
  if (!rows.length) return null;
  const newestDone = rows.filter((b) => b.status === "done").sort((a, b) => String(b.finished_at).localeCompare(String(a.finished_at)))[0]?.id;
  const del = async (b) => {
    if (!window.confirm(`Usunąć obraz z ${(b.started_at || "").slice(0, 10)}? Zwolni to miejsce na dysku mastera.`)) return;
    try { await api.delete(`/license/image-builds/${b.id}`); toast.success("Obraz usunięty"); setRk((k) => k + 1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="mb-3 text-[11px] space-y-1" data-testid="licmon-image-builds">
      <p className="text-slate-500">Master trzyma automatycznie 2 najnowsze obrazy — starsze i nieudane usuwa sam po każdej udanej budowie. Najnowszy (z niego aktualizują się klienci) jest chroniony.</p>
      {rows.slice(0, 5).map((b) => (
        <div key={b.id} className="flex flex-wrap items-center gap-2 border border-slate-800 rounded px-2 py-1" data-testid={`licmon-image-${b.id}`}>
          <span className="text-slate-500">{(b.started_at || "").slice(0, 16).replace("T", " ")}</span>
          <span>{b.company}</span>
          <span className={b.status === "done" ? "text-emerald-300" : b.status === "failed" ? "text-red-300" : "text-amber-300"}>
            {b.status === "running" ? `⏳ ${b.stage}` : b.status === "done" ? `Gotowe (${Math.round((b.size || 0) / 1048576)} MB)` : `Błąd: ${(b.error || "").slice(-160)}`}
          </span>
          {b.status === "done" && <button onClick={() => openDownload(`/license/image-builds/${b.id}/download`)} className="text-cyan-300 hover:underline" data-testid={`licmon-image-dl-${b.id}`}>Pobierz paczkę Docker dla klienta</button>}
          {b.status === "done" && b.id === newestDone && <span className="text-emerald-400/80" data-testid={`licmon-image-current-${b.id}`}>aktualny dla klientów</span>}
          {b.status !== "running" && b.id !== newestDone && <button onClick={() => del(b)} className="text-red-300 hover:underline" data-testid={`licmon-image-del-${b.id}`}>Usuń</button>}
        </div>
      ))}
    </div>
  );
}

function Builder() {
  const [lics, setLics] = useState([]);
  const [id, setId] = useState("");
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);
  useEffect(() => { api.get("/license/issued").then((r) => setLics(r.data.filter((l) => !l.revoked && !l.renewed))).catch(() => {}); }, []);
  const build = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/license/${id}/build-package`); toast.success(`Paczka oznakowana: ${data.build_id}`); openDownload(`/license/builds/${data.build_id}/download`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  const buildImage = async () => {
    try { await api.post(`/license/build-image`); toast.success("Budowa wspólnego obrazu rozpoczęta — potrwa 10–20 minut, możesz zamknąć okno"); setRk((k) => k + 1); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <>
    <div className="flex flex-wrap gap-2 items-center mb-2 text-[11px] p-2 rounded border border-slate-700 bg-slate-900/40" data-testid="licmon-builder">
      <Package size={13} className="text-cyan-400" /><span>Oznakowana paczka kliencka:</span>
      <select value={id} onChange={(e) => setId(e.target.value)} className="bg-slate-900 border border-slate-700 rounded px-2 py-1" data-testid="licmon-build-lic">
        <option value="">— licencja —</option>{lics.map((l) => <option key={l.id} value={l.id}>{l.company} ({l.expires || "bezterminowa"}){(l.builds || []).length ? ` · paczek: ${l.builds.length}` : ""}</option>)}</select>
      <button disabled={!id || busy} onClick={build} className="bg-cyan-700 hover:bg-cyan-600 disabled:opacity-40 rounded px-3 py-1" data-testid="licmon-build-btn">{busy ? "Buduję…" : "Zbuduj i pobierz ZIP"}</button>
      <button onClick={buildImage} title="Jeden obraz Docker bez kodu źródłowego dla wszystkich klientów — znak instalacji nadawany przy aktywacji kodem" className="bg-emerald-700 hover:bg-emerald-600 rounded px-3 py-1" data-testid="licmon-build-image-btn">Zbuduj wspólny obraz Docker</button>
    </div>
    <ImageBuilds reload={rk} />
    </>
  );
}

export function LicenseMonitor({ t, open, onToggle }) {
  const [st, setSt] = useState(null);
  const [rows, setRows] = useState([]);
  const [rk, setRk] = useState(0);
  const load = useCallback(() => {
    setRk((k) => k + 1);
    api.get("/license/contacts/stats").then((r) => setSt(r.data)).catch(() => {});
    api.get("/license/installations").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);
  const revoke = async (r) => {
    if (!window.confirm(`Odwołać licencję „${r.company}”? Instalacja zablokuje się przy następnym kontakcie z masterem.`)) return;
    try { await api.post("/license/revoke", { key_hash: r.key_hash }); toast.success("Licencja odwołana"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Section icon={Radar} iconColor="text-red-400" title={t("Monitoring instalacji")} open={open} onToggle={onToggle} testid="settings-sec-licmonitor">
      <Tiles st={st} />
      <Builder />
      {open && <InstallMap reloadKey={rk} />}
      <Installs rows={rows} onRevoke={revoke} />
      <Log />
    </Section>
  );
}
